import Dexie, { type EntityTable, type Transaction } from 'dexie';
import type {
  AppSettings,
  BodyLog,
  DeletedRecord,
  DeviceConfig,
  Exercise,
  ProgressPhoto,
  Routine,
  Session,
  Snapshot,
  WorkoutSet
} from './types';

export const SCHEMA_VERSION = 3;

const schemaV1 = {
  exercises: 'id, name, *muscles, equipment, updatedAt',
  routines: 'id, dayOfCycle, updatedAt',
  sessions: 'id, routineId, startedAt, localDate, finishedAt, updatedAt',
  sets: 'id, sessionId, exerciseId, [sessionId+exerciseId], setNumber, completedAt, updatedAt',
  bodyLog: 'id, date, updatedAt',
  photos: 'id, createdAt, localDate',
  settings: 'id, updatedAt',
  snapshots: 'id, createdAt',
  deletions: 'id, table, recordId, updatedAt'
};

const schemaV3 = { ...schemaV1, deviceConfig: 'id' };
const snapshotTables = ['exercises', 'routines', 'sessions', 'sets', 'bodyLog', 'settings', 'deletions'] as const;

async function snapshotMigration(transaction: Transaction, version: number): Promise<void> {
  const payload: Record<string, unknown[]> = {};
  for (const table of snapshotTables) payload[table] = await transaction.table(table).toArray();
  await transaction.table('snapshots').put({
    id: `snapshot-migration-v${version}-${crypto.randomUUID()}`,
    schemaVersion: version - 1,
    createdAt: Date.now(),
    reason: `before-migration-v${version}`,
    payload: JSON.stringify(payload)
  });
}

export class EliteDatabase extends Dexie {
  exercises!: EntityTable<Exercise, 'id'>;
  routines!: EntityTable<Routine, 'id'>;
  sessions!: EntityTable<Session, 'id'>;
  sets!: EntityTable<WorkoutSet, 'id'>;
  bodyLog!: EntityTable<BodyLog, 'id'>;
  photos!: EntityTable<ProgressPhoto, 'id'>;
  settings!: EntityTable<AppSettings, 'id'>;
  deviceConfig!: EntityTable<DeviceConfig, 'id'>;
  snapshots!: EntityTable<Snapshot, 'id'>;
  deletions!: EntityTable<DeletedRecord, 'id'>;

  constructor(name = 'sampson-swole-elite-v2') {
    super(name);
    this.version(1).stores(schemaV1);
    this.version(2).stores(schemaV1).upgrade(async transaction => {
      await snapshotMigration(transaction, 2);
      const bodyweightIds = new Set(['air-squat', 'dips', 'supinated-pull-up']);
      await transaction.table('exercises').toCollection().modify((exercise: Exercise) => {
        exercise.loadType = bodyweightIds.has(exercise.id) ? 'bodyweight' : 'external';
      });
    });
    this.version(3).stores(schemaV3).upgrade(async transaction => {
      await snapshotMigration(transaction, 3);
      const settings = await transaction.table('settings').get('primary') as Record<string, unknown> | undefined;
      await transaction.table('deviceConfig').put({
        id: 'device',
        syncApiUrl: String(settings?.syncApiUrl || ''),
        syncToken: String(settings?.syncToken || ''),
        lastPushedAt: Number(settings?.lastSyncAt || 0),
        lastPullCursor: Number(settings?.lastSyncAt || 0)
      });
      if (settings) {
        delete settings.syncApiUrl;
        delete settings.syncToken;
        delete settings.lastSyncAt;
        settings.units = 'imperial';
        settings.microPlates ??= false;
        settings.soundHaptics ??= true;
        await transaction.table('settings').put(settings);
      }
    });
  }
}

export const db = new EliteDatabase();

export function localDateFrom(timestamp = Date.now()): string {
  const date = new Date(timestamp);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function createId(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

export async function initializeDatabase(database = db): Promise<void> {
  await database.open();
  const [exerciseCount, routineCount, settings, deviceConfig] = await Promise.all([
    database.exercises.count(),
    database.routines.count(),
    database.settings.get('primary'),
    database.deviceConfig.get('device')
  ]);
  const now = Date.now();
  const starterData = exerciseCount === 0 || routineCount === 0 ? await import('./program') : undefined;
  await database.transaction('rw', database.exercises, database.routines, database.settings, database.deviceConfig, async () => {
    if (exerciseCount === 0) await database.exercises.bulkPut((starterData?.starterExercises || []).map(item => ({ ...item, updatedAt: now })));
    if (routineCount === 0) await database.routines.bulkPut((starterData?.starterRoutines || []).map(item => ({ ...item, updatedAt: now })));
    if (!settings) {
      await database.settings.put({
        id: 'primary',
        units: 'imperial',
        barWeight: 45,
        plateInventory: [45, 35, 25, 10, 5, 2.5],
        defaultCompoundRest: 180,
        defaultIsolationRest: 90,
        weekStartsOn: 1,
        scheduleAnchor: localDateFrom(),
        nextRoutineDay: 1,
        microPlates: false,
        soundHaptics: true,
        updatedAt: now
      });
    }
    if (!deviceConfig) {
      await database.deviceConfig.put({
        id: 'device',
        syncApiUrl: import.meta.env.VITE_SYNC_API_URL || '',
        syncToken: import.meta.env.VITE_SYNC_DEVICE_TOKEN || '',
        lastPushedAt: 0,
        lastPullCursor: 0,
        pushEnabled: false
      });
    }
  });
  try {
    if (navigator.storage?.persist) await navigator.storage.persist();
  } catch {
    // IndexedDB still works when persistent storage cannot be granted.
  }
}

export async function getActiveSession(database = db): Promise<Session | undefined> {
  return database.sessions.filter(session => !session.finishedAt).first();
}

export async function lastCompletedSet(exerciseId: string, setNumber?: number, database = db): Promise<WorkoutSet | undefined> {
  const values = await database.sets.where('exerciseId').equals(exerciseId).filter(set => Boolean(set.completedAt) && (setNumber == null || set.setNumber === setNumber)).toArray();
  return values.sort((a, b) => (b.completedAt || 0) - (a.completedAt || 0))[0];
}

export class ActiveSessionExists extends Error {
  constructor() {
    super('A workout is already in progress. Resume it before starting another.');
    this.name = 'ActiveSessionExists';
  }
}

export async function startSession(routineId: string, database = db): Promise<Session> {
  const routine = await database.routines.get(routineId);
  if (!routine || routine.exerciseRefs.length === 0) throw new Error('This routine does not contain a workout.');
  const exercises = await database.exercises.toArray();
  const exerciseMap = new Map(exercises.map(exercise => [exercise.id, exercise]));
  const now = Date.now();
  const session: Session = { id: createId('session'), routineId, startedAt: now, localDate: localDateFrom(now), updatedAt: now };
  const sets: WorkoutSet[] = [];
  for (const reference of routine.exerciseRefs) {
    const exercise = exerciseMap.get(reference.exerciseId);
    if (!exercise) continue;
    for (let index = 0; index < reference.sets; index += 1) {
      const prior = await lastCompletedSet(reference.exerciseId, index + 1, database);
      sets.push({
        id: createId('set'),
        sessionId: session.id,
        exerciseId: reference.exerciseId,
        setNumber: index + 1,
        type: reference.repTarget.label === 'AMRAP' ? 'amrap' as const : 'working' as const,
        weight: prior?.weight ?? 0,
        unit: exercise.unit,
        reps: prior?.reps ?? reference.repTarget.min,
        rpe: prior?.rpe,
        updatedAt: now
      });
    }
  }
  await database.transaction('rw', database.sessions, database.sets, async () => {
    if (await getActiveSession(database)) throw new ActiveSessionExists();
    await database.sessions.add(session);
    await database.sets.bulkAdd(sets);
  });
  return session;
}

export async function updateSet(id: string, changes: Partial<WorkoutSet>, database = db): Promise<void> {
  await database.sets.update(id, { ...changes, updatedAt: Date.now() });
}

export async function addSet(sessionId: string, exerciseId: string, database = db): Promise<WorkoutSet> {
  const existing = await database.sets.where('[sessionId+exerciseId]').equals([sessionId, exerciseId]).toArray();
  const previous = existing.sort((a, b) => b.setNumber - a.setNumber)[0];
  const now = Date.now();
  const set: WorkoutSet = {
    id: createId('set'), sessionId, exerciseId, setNumber: (previous?.setNumber || 0) + 1,
    type: previous?.type || 'working', weight: previous?.weight || 0, unit: previous?.unit || 'lb',
    reps: previous?.reps || 8, rpe: previous?.rpe, updatedAt: now
  };
  await database.sets.add(set);
  return set;
}

export async function addWarmupSets(sessionId: string, exerciseId: string, plan: Array<{ weight: number; reps: number }>, database = db): Promise<void> {
  if (!plan.length) return;
  const existing = await database.sets.where('[sessionId+exerciseId]').equals([sessionId, exerciseId]).sortBy('setNumber');
  const now = Date.now();
  const warmups = plan.map((item, index): WorkoutSet => ({
    id: createId('set'), sessionId, exerciseId, setNumber: index + 1, type: 'warmup', weight: item.weight,
    unit: existing[0]?.unit || 'lb', reps: item.reps, updatedAt: now
  }));
  await database.transaction('rw', database.sets, async () => {
    await Promise.all(existing.map((set, index) => database.sets.update(set.id, { setNumber: index + 1 + warmups.length, updatedAt: now })));
    await database.sets.bulkAdd(warmups);
  });
}

export async function removeSet(set: WorkoutSet, database = db): Promise<void> {
  const now = Date.now();
  await database.transaction('rw', database.sets, database.deletions, async () => {
    await database.sets.delete(set.id);
    await database.deletions.put({ id: `sets:${set.id}`, table: 'sets', recordId: set.id, updatedAt: now });
    const remaining = await database.sets.where('[sessionId+exerciseId]').equals([set.sessionId, set.exerciseId]).sortBy('setNumber');
    await Promise.all(remaining.map((item, index) => database.sets.update(item.id, { setNumber: index + 1, updatedAt: now })));
  });
}

export async function restoreSet(set: WorkoutSet, database = db): Promise<void> {
  const now = Date.now();
  await database.transaction('rw', database.sets, database.deletions, async () => {
    await database.sets.put({ ...set, updatedAt: now });
    const group = await database.sets.where('[sessionId+exerciseId]').equals([set.sessionId, set.exerciseId]).toArray();
    group.sort((a, b) => a.setNumber - b.setNumber || (a.id === set.id ? -1 : b.id === set.id ? 1 : a.id.localeCompare(b.id)));
    await Promise.all(group.map((item, index) => database.sets.update(item.id, { setNumber: index + 1, updatedAt: now })));
    await database.deletions.delete(`sets:${set.id}`);
  });
}

export async function finishSession(sessionId: string, notes = '', database = db): Promise<Session> {
  const session = await database.sessions.get(sessionId);
  if (!session) throw new Error('Session not found.');
  const completedCount = await database.sets.where('sessionId').equals(sessionId).filter(set => Boolean(set.completedAt)).count();
  if (completedCount === 0) throw new Error('Nothing logged. Discard this session or complete at least one set.');
  const now = Date.now();
  const finished: Session = { ...session, finishedAt: now, notes, restEndsAt: undefined, restExerciseId: undefined, restSetId: undefined, updatedAt: now };
  const [routine, settings] = await Promise.all([database.routines.get(session.routineId), database.settings.get('primary')]);
  await database.transaction('rw', database.sessions, database.settings, async () => {
    await database.sessions.put(finished);
    if (settings && routine) await database.settings.put({ ...settings, nextRoutineDay: cycleDay(routine.dayOfCycle + 1), updatedAt: now });
  });
  return finished;
}

const cycleDay = (value: number) => ((value - 1) % 7 + 7) % 7 + 1;

export async function advanceRotation(database = db): Promise<AppSettings> {
  const settings = await database.settings.get('primary');
  if (!settings) throw new Error('Settings are not initialized.');
  return saveSettings({ nextRoutineDay: cycleDay(settings.nextRoutineDay + 1) }, database);
}

export async function discardSession(sessionId: string, database = db): Promise<void> {
  const now = Date.now();
  await database.transaction('rw', database.sessions, database.sets, database.deletions, async () => {
    const sets = await database.sets.where('sessionId').equals(sessionId).toArray();
    await database.sets.where('sessionId').equals(sessionId).delete();
    await database.sessions.delete(sessionId);
    await database.deletions.bulkPut([
      { id: `sessions:${sessionId}`, table: 'sessions', recordId: sessionId, updatedAt: now },
      ...sets.map(set => ({ id: `sets:${set.id}`, table: 'sets', recordId: set.id, updatedAt: now }))
    ]);
  });
}

export async function updateHistoricalSession(sessionId: string, changes: Pick<Session, 'localDate' | 'notes'>, database = db): Promise<void> {
  const session = await database.sessions.get(sessionId);
  if (!session?.finishedAt) throw new Error('Only completed sessions can be edited here.');
  await database.sessions.update(sessionId, { ...changes, updatedAt: Date.now() });
}

export async function addBodyEntry(entry: Omit<BodyLog, 'id' | 'updatedAt'>, photo?: Blob, database = db): Promise<BodyLog> {
  const now = Date.now();
  let photoBlobId: string | undefined;
  if (photo) {
    photoBlobId = createId('photo');
    await database.photos.put({ id: photoBlobId, blob: await compressProgressPhoto(photo), createdAt: now, localDate: entry.date });
  }
  const record: BodyLog = { ...entry, id: createId('body'), photoBlobId, updatedAt: now };
  await database.bodyLog.put(record);
  return record;
}

async function compressProgressPhoto(photo: Blob): Promise<Blob> {
  try {
    const bitmap = await createImageBitmap(photo);
    const scale = Math.min(1, 1200 / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext('2d', { alpha: false });
    if (!context) return photo;
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    return await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/webp', 0.84)) || photo;
  } catch {
    return photo;
  }
}

type BackupTables = { exercises: Exercise[]; routines: Routine[]; sessions: Session[]; sets: WorkoutSet[]; bodyLog: BodyLog[]; settings: AppSettings[]; deletions: DeletedRecord[] };
export type BackupPayload = { app: 'sampson-swole-elite'; schemaVersion: number; exportedAt: number; tables: BackupTables; photos: Array<{ id: string; createdAt: number; localDate: string; file: string; type: string }> };

async function collectBackupTables(database = db): Promise<BackupTables> {
  const [exercises, routines, sessions, sets, bodyLog, settings, deletions] = await Promise.all([
    database.exercises.toArray(), database.routines.toArray(), database.sessions.toArray(), database.sets.toArray(),
    database.bodyLog.toArray(), database.settings.toArray(), database.deletions.toArray()
  ]);
  return { exercises, routines, sessions, sets, bodyLog, settings, deletions };
}

export async function createSnapshot(reason: string, database = db): Promise<Snapshot> {
  const snapshot: Snapshot = { id: createId('snapshot'), schemaVersion: SCHEMA_VERSION, createdAt: Date.now(), reason, payload: JSON.stringify(await collectBackupTables(database)) };
  await database.snapshots.put(snapshot);
  return snapshot;
}

export async function exportBackup(database = db): Promise<Blob> {
  const { strToU8, zipSync } = await import('fflate');
  const photos = await database.photos.toArray();
  const metadata: BackupPayload = {
    app: 'sampson-swole-elite', schemaVersion: SCHEMA_VERSION, exportedAt: Date.now(), tables: await collectBackupTables(database),
    photos: photos.map(photo => ({ id: photo.id, createdAt: photo.createdAt, localDate: photo.localDate, file: `photos/${photo.id}`, type: photo.blob.type || 'image/webp' }))
  };
  const files: Record<string, Uint8Array> = { 'backup.json': strToU8(JSON.stringify(metadata, null, 2)) };
  for (const photo of photos) files[`photos/${photo.id}`] = new Uint8Array(await photo.blob.arrayBuffer());
  return new Blob([zipSync(files, { level: 6 })], { type: 'application/zip' });
}

export async function importBackup(file: File, database = db): Promise<void> {
  const { strFromU8, unzipSync } = await import('fflate');
  await createSnapshot('before-import', database);
  const archive = unzipSync(new Uint8Array(await file.arrayBuffer()));
  const metadataFile = archive['backup.json'];
  if (!metadataFile) throw new Error('The backup does not contain backup.json.');
  const payload = JSON.parse(strFromU8(metadataFile)) as BackupPayload;
  if (payload.app !== 'sampson-swole-elite' || payload.schemaVersion > SCHEMA_VERSION) throw new Error('This backup is not compatible with the current app.');
  const photos: ProgressPhoto[] = payload.photos.map(photo => {
    const bytes = archive[photo.file];
    if (!bytes) throw new Error(`Missing photo file: ${photo.file}`);
    return { id: photo.id, blob: new Blob([bytes], { type: photo.type }), createdAt: photo.createdAt, localDate: photo.localDate };
  });
  await database.transaction('rw', [database.exercises, database.routines, database.sessions, database.sets, database.bodyLog, database.photos, database.settings, database.deletions], async () => {
    await Promise.all([database.exercises.clear(), database.routines.clear(), database.sessions.clear(), database.sets.clear(), database.bodyLog.clear(), database.photos.clear(), database.settings.clear(), database.deletions.clear()]);
    await database.exercises.bulkPut(payload.tables.exercises.map(exercise => ({ ...exercise, loadType: exercise.loadType || (exercise.unit === 'bodyweight' ? 'bodyweight' : 'external') })));
    await database.routines.bulkPut(payload.tables.routines);
    await database.sessions.bulkPut(payload.tables.sessions);
    await database.sets.bulkPut(payload.tables.sets);
    await database.bodyLog.bulkPut(payload.tables.bodyLog);
    await database.settings.bulkPut(payload.tables.settings.map(settings => ({ ...settings, units: 'imperial', microPlates: settings.microPlates ?? false, soundHaptics: settings.soundHaptics ?? true })));
    await database.deletions.bulkPut(payload.tables.deletions);
    await database.photos.bulkPut(photos);
  });
}

export async function saveSettings(changes: Partial<AppSettings>, database = db): Promise<AppSettings> {
  const current = await database.settings.get('primary');
  if (!current) throw new Error('Settings are not initialized.');
  const next = { ...current, ...changes, id: 'primary' as const, units: 'imperial' as const, updatedAt: Date.now() };
  await database.settings.put(next);
  return next;
}

export async function saveDeviceConfig(changes: Partial<DeviceConfig>, database = db): Promise<DeviceConfig> {
  const current = await database.deviceConfig.get('device') || { id: 'device' as const, syncApiUrl: '', syncToken: '', lastPushedAt: 0, lastPullCursor: 0 };
  const next = { ...current, ...changes, id: 'device' as const };
  await database.deviceConfig.put(next);
  return next;
}

export async function getProgressPhotos(ids?: string[], database = db): Promise<ProgressPhoto[]> {
  if (!ids) return database.photos.orderBy('createdAt').reverse().toArray();
  return database.photos.where('id').anyOf(ids).toArray();
}

export async function resetTrainingHistory(database = db): Promise<void> {
  await createSnapshot('before-history-reset', database);
  await database.transaction('rw', database.sessions, database.sets, database.bodyLog, database.photos, database.deletions, async () => {
    await Promise.all([database.sessions.clear(), database.sets.clear(), database.bodyLog.clear(), database.photos.clear(), database.deletions.clear()]);
  });
}
